import { formatRelativeTime } from "@/features/workspace/hooks/formatRelativeTime";
import type { Canvas } from "@ando/core";
import { Link } from "@tanstack/react-router";
import { BookOpenText, Loader2 } from "lucide-react";
import type * as React from "react";
import { CanvasStatusChip } from "./CanvasStatusChip.js";

/**
 * CanvasCard — list row for the canvases hub.
 *
 * v1 ref: ``ando-frontend/app/components/canvases/CanvasCard.tsx`` (grid
 * card) + ``CanvasRow.tsx`` (list row). v2 unifies them into a single row
 * shape — the densitytoggle / grid layout from v1 is a UX nice-to-have we
 * can re-introduce once the basic flow is wired (Phase F-LATER).
 *
 * Renders subtitle preview + scope + entity_type + compile status, mirrors
 * the v2 ``conversations.tsx`` row pattern so the visual language stays
 * consistent across hubs.
 *
 * Click behavior (May 25, 2026 fix — Tommaso "Thingowex no-op"):
 *   Default navigates to the canonical detail route
 *   ``/workspace/canvases/$canvasId`` via TanStack ``<Link>`` (real anchor,
 *   middle-click + cmd-click open in new tab, prefetchable, accessible by
 *   default). The optional ``onClick`` override is kept for callers that
 *   need to plug in custom analytics or a modal preview instead.
 */
export const CanvasCard: React.FC<{
  canvas: Canvas;
  onClick?: () => void;
}> = ({ canvas, onClick }) => {
  const time = formatRelativeTime(canvas.compiled_at ?? canvas.updated_at ?? canvas.created_at);
  const subtitle = canvas.subtitle?.trim() || "—";
  const isCompiling = canvas.compile_status === "compiling";
  // "ready" is the implicit/default state — surfacing a green "ready"
  // chip on every healthy row creates visual noise that drowns out the
  // chips that DO carry signal (compiling, failed, stale, archived).
  // Same pattern used in conversations.tsx for the "read" state.
  const showStatusChip = canvas.compile_status !== "ready";

  const inner = (
    <>
      <span
        aria-hidden
        className="mt-0.5 flex shrink-0 items-center justify-center text-ink-3 transition-colors group-hover:text-ink-1"
      >
        {isCompiling ? (
          <Loader2 size={15} strokeWidth={1.75} className="animate-spin" />
        ) : (
          <BookOpenText size={15} strokeWidth={1.75} />
        )}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium text-[13px] text-ink-1">{canvas.title}</span>
        <span className="mt-0.5 block truncate text-[12px] text-ink-3">{subtitle}</span>
        <span className="mt-1 flex items-center gap-2 text-[10px] text-ink-3 tabular-nums">
          <span className="uppercase tracking-[0.05em]">{canvas.entity_type}</span>
          <span aria-hidden>·</span>
          <span className="uppercase tracking-[0.05em]">{canvas.scope}</span>
          {time ? (
            <>
              <span aria-hidden>·</span>
              <span>{time}</span>
            </>
          ) : null}
        </span>
      </span>
      {showStatusChip ? <CanvasStatusChip status={canvas.compile_status} /> : null}
    </>
  );

  // Shared chrome for both the link and button surfaces — keep them in
  // sync visually so the row never flickers between "clickable" and
  // "navigable" styling.
  const chrome =
    "group flex w-full items-start gap-3 rounded-lg px-3 py-2.5 text-left " +
    "transition-all hover:translate-x-[2px] " +
    "focus-visible:outline-none focus-visible:ring-2 " +
    "focus-visible:ring-ando-orange focus-visible:ring-offset-2 " +
    "focus-visible:ring-offset-cream";

  // Override path — caller wants a custom click (drawer, modal, …).
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={chrome}>
        {inner}
      </button>
    );
  }

  // Default — real anchor → detail route. Better UX than a button
  // (cmd-click for new tab, middle-click, hover preview, screen-reader
  // announces "link" instead of "button").
  return (
    <Link to="/workspace/canvases/$canvasId" params={{ canvasId: canvas.id }} className={chrome}>
      {inner}
    </Link>
  );
};
CanvasCard.displayName = "CanvasCard";
